/**
 * Route validation utilities for fixed route fleet generation
 * 
 * Validates routes before fleet calculation to ensure data integrity.
 */

import type { FixedRoute } from '../../types/fixedRoutes';
import type { ValidationResult, MixedFleetConfig, FleetResult, VehicleAssignment } from './fleetTypes';
import { calculateRouteLoopDuration } from './vehicleCalculator';
import { validateTransitions } from './mixedFleetOptimizer';

/**
 * Validate a single route
 * 
 * @param route - The fixed route to validate
 * @param trafficMultiplier - Traffic multiplier for loop duration calculation (default: 1.0)
 * @returns Validation result with errors and warnings
 */
export function validateRoute(
  route: FixedRoute,
  trafficMultiplier: number = 1.0
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check minimum stops
  if (!route.stops || route.stops.length < 2) {
    errors.push('Route must have at least 2 stops');
  }

  // Validate start time
  const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
  if (!route.startTime || !timeRegex.test(route.startTime)) {
    errors.push('Invalid or missing start time (must be HH:mm format)');
  }

  // Validate end time (if specified)
  if (route.endTime && !timeRegex.test(route.endTime)) {
    errors.push('Invalid end time format (must be HH:mm format)');
  }

  // Circulator routes require end time
  if (route.isCirculator && !route.endTime) {
    errors.push('Circulator routes require an end time');
  }

  // Validate segments
  if (!route.segments || route.segments.length === 0) {
    errors.push('Route must have at least one segment with travel times');
  } else {
    // Check that segments have travel times
    const segmentsWithoutTimes = route.segments.filter(
      (seg) => !seg.travelTimeMinutes || seg.travelTimeMinutes <= 0
    );
    if (segmentsWithoutTimes.length > 0) {
      errors.push('Some segments are missing travel times');
    }
  }

  // For circulators: validate loop duration can be calculated
  if (route.isCirculator && route.stops && route.stops.length >= 2 && route.segments && route.segments.length > 0) {
    try {
      const loopDuration = calculateRouteLoopDuration(route, trafficMultiplier);
      if (loopDuration <= 0) {
        warnings.push(`Calculated loop duration is ${loopDuration} minutes (may indicate missing data)`);
      } else if (loopDuration < 5) {
        warnings.push(`Very short loop duration (${loopDuration} minutes) - may indicate incorrect configuration`);
      } else if (loopDuration > 240) {
        warnings.push(`Very long loop duration (${loopDuration} minutes = ${Math.round(loopDuration / 60)} hours)`);
      }
    } catch (error) {
      errors.push(`Cannot calculate loop duration - ${error instanceof Error ? error.message : 'unknown error'}`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate multiple routes
 * 
 * @param routes - Array of fixed routes to validate
 * @param trafficMultiplier - Traffic multiplier for loop duration calculation (default: 1.0)
 * @returns Array of validation results, one per route
 */
export function validateRoutes(
  routes: FixedRoute[],
  trafficMultiplier: number = 1.0
): ValidationResult[] {
  return routes.map((route) => validateRoute(route, trafficMultiplier));
}

/**
 * Validate mixed fleet configuration
 * 
 * @param config - Mixed fleet configuration to validate
 * @returns Validation result with errors and warnings
 */
export function validateMixedFleetConfig(config: MixedFleetConfig): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validate priority mode
  if (!config.priorityMode || !['fixed-first', 'on-demand-first', 'equal', 'weighted'].includes(config.priorityMode)) {
    errors.push('Invalid priority mode. Must be one of: fixed-first, on-demand-first, equal, weighted');
  }

  // Validate weights for weighted mode
  if (config.priorityMode === 'weighted') {
    const fixedWeight = config.fixedRouteWeight ?? 0;
    const onDemandWeight = config.onDemandWeight ?? 0;
    
    if (fixedWeight < 0 || fixedWeight > 100) {
      errors.push('Fixed route weight must be between 0 and 100');
    }
    if (onDemandWeight < 0 || onDemandWeight > 100) {
      errors.push('On-demand weight must be between 0 and 100');
    }
    if (fixedWeight === 0 && onDemandWeight === 0) {
      errors.push('At least one weight must be greater than 0 for weighted mode');
    }
    // Warn if weights don't sum to 100 (but allow - will normalize)
    const sum = fixedWeight + onDemandWeight;
    if (sum !== 100 && sum > 0) {
      warnings.push(`Weights sum to ${sum}% (will be normalized to 100%)`);
    }
  }

  // Validate transition time
  if (config.transitionTimeMinutes !== undefined) {
    if (config.transitionTimeMinutes < 0) {
      errors.push('Transition time cannot be negative');
    }
    if (config.transitionTimeMinutes > 60) {
      warnings.push(`Transition time is ${config.transitionTimeMinutes} minutes (may be excessive)`);
    }
  }

  // Validate at least one route or trip source
  const hasRoutes = config.routes && config.routes.length > 0;
  // Note: trips validation would need to be done separately as trips come from different sources

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate mixed fleet result
 * 
 * @param fleetResult - Fleet result to validate
 * @param config - Mixed fleet configuration used
 * @returns Validation result with errors and warnings
 */
export function validateMixedFleetResult(
  fleetResult: FleetResult,
  config: MixedFleetConfig
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!fleetResult) {
    errors.push('Fleet result is null or undefined');
    return { isValid: false, errors, warnings };
  }

  if (!fleetResult.vehicleAssignments || fleetResult.vehicleAssignments.length === 0) {
    errors.push('Fleet result contains no vehicle assignments');
    return { isValid: false, errors, warnings };
  }

  // Validate all vehicle assignments
  fleetResult.vehicleAssignments.forEach((assignment, index) => {
    const vehiclePrefix = `Vehicle "${assignment.vehicleName || assignment.vehicleId}" (${index + 1})`;

    // Validate transitions
    const transitionValidation = validateTransitions(assignment);
    if (!transitionValidation.isValid) {
      errors.push(`${vehiclePrefix}: ${transitionValidation.errors.join('; ')}`);
    }
    if (transitionValidation.warnings.length > 0) {
      warnings.push(`${vehiclePrefix}: ${transitionValidation.warnings.join('; ')}`);
    }

    // Check if assignment has both fixed and on-demand periods (mixed)
    const hasFixed = assignment.routeAssignments.length > 0;
    const hasOnDemand = (assignment as any).onDemandPeriods && (assignment as any).onDemandPeriods.length > 0;

    if (hasFixed && hasOnDemand) {
      // Validate transition times are reasonable
      const transitions = (assignment as any).transitions || [];
      transitions.forEach((transition: any, transIndex: number) => {
        const transitionDuration = parseTransitionDuration(transition.start, transition.end);
        if (config.transitionTimeMinutes !== undefined && transitionDuration > config.transitionTimeMinutes + 30) {
          warnings.push(`${vehiclePrefix}: Transition ${transIndex + 1} duration (${transitionDuration} min) exceeds configured transition time (${config.transitionTimeMinutes} min)`);
        }
      });
    }
  });

  // Validate fixed route requirements are met (if priority mode requires it)
  if (config.priorityMode === 'fixed-first' && fleetResult.mixedFleetSummary) {
    if (!fleetResult.mixedFleetSummary.allFixedRouteRequirementsMet) {
      errors.push('Fixed-first priority mode requires all fixed route requirements to be met');
    }
  }

  // Validate on-demand coverage (if priority mode requires it)
  if (config.priorityMode === 'on-demand-first' && fleetResult.mixedFleetSummary) {
    if (fleetResult.mixedFleetSummary.onDemandCoveragePercent < 80) {
      warnings.push(`On-demand coverage is only ${fleetResult.mixedFleetSummary.onDemandCoveragePercent.toFixed(0)}% (may be insufficient)`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Helper function to parse time duration between two time strings
 */
function parseTransitionDuration(start: string, end: string): number {
  const parseTime = (timeStr: string): number => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const startMinutes = parseTime(start);
  const endMinutes = parseTime(end);
  return endMinutes - startMinutes;
}

